import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CONFIG, INITIAL, RESETTABLE, type DemoState, type MemberAlert } from './state'
import { ARCHITECTURES, type ArchitectureId } from './architecture'
import { makeBreachAlert, makeFeatureAlert, makeThreat, type FeatureId, type ThreatKind } from './data'
import { FLOW_DEPENDS_ON_ARCHITECTURE, flowPreset } from './flows'

/** Primitives the derived-values layer drives the demo through. */
export type DemoApi = {
  set: (patch: Partial<DemoState>) => void
  update: (fn: (prev: DemoState) => Partial<DemoState>) => void
  toast: (which: 'm' | 'k', text: string) => void
  block: (kind: ThreatKind) => void
  setMemberFeature: (feature: FeatureId, on: boolean) => void
  restoreFeature: (feature: FeatureId) => void
  raiseBreach: (account: string) => void
  reset: () => void
  setArchitecture: (id: ArchitectureId) => void
}

export function useDemo(): { state: DemoState; api: DemoApi } {
  const [state, setState] = useState<DemoState>(INITIAL)

  // Reading state through a ref keeps the api object stable while still letting
  // deferred work (the alert-delivery delay) see the latest values.
  const ref = useRef(state)
  ref.current = state

  const timers = useRef<{ m?: number; k?: number }>({})

  const set = useCallback((patch: Partial<DemoState>) => {
    setState((prev) => ({ ...prev, ...patch }))
  }, [])

  const update = useCallback((fn: (prev: DemoState) => Partial<DemoState>) => {
    setState((prev) => ({ ...prev, ...fn(prev) }))
  }, [])

  const toast = useCallback((which: 'm' | 'k', text: string) => {
    const key = which === 'm' ? 'toastM' : 'toastK'
    setState((prev) => ({ ...prev, [key]: text }))
    window.clearTimeout(timers.current[which])
    timers.current[which] = window.setTimeout(() => {
      setState((prev) => ({ ...prev, [key]: null }))
    }, CONFIG.toastMs)
  }, [])

  const reset = useCallback(() => {
    window.clearTimeout(timers.current.m)
    window.clearTimeout(timers.current.k)
    setState((prev) => ({ ...prev, ...RESETTABLE }))
  }, [])

  const setArchitecture = useCallback((arch: ArchitectureId) => {
    setState((prev) => {
      // Purchase and invite behave the same either way, so switching there is
      // a no-op beyond the label — no reason to throw away where they are.
      if (!FLOW_DEPENDS_ON_ARCHITECTURE[prev.flow]) return { ...prev, arch }

      // Past that point the story has to restart: attributed alerts are
      // something a zero-knowledge backend could never have produced. What the
      // manager has *named* survives though — the group's name and any aliases
      // are theirs, and neither model has an opinion about them.
      window.clearTimeout(timers.current.m)
      window.clearTimeout(timers.current.k)
      return {
        ...prev,
        ...RESETTABLE,
        ...flowPreset(prev.flow, arch),
        arch,
        groupName: prev.groupName,
        aliases: prev.aliases,
      }
    })
  }, [])

  /** A demo trigger: the member's device blocks something. */
  const block = useCallback(
    (kind: ThreatKind) => {
      const before = ref.current
      const arch = ARCHITECTURES[before.arch]
      const threat = makeThreat(kind, CONFIG.memberDevice)

      setState((prev) => ({
        ...prev,
        flow: 4,
        k: 'blocked',
        blocked: threat,
        kBlockCount: prev.kBlockCount + 1,
        kAlerts: [
          { threat, shared: prev.deviceAccepted && !prev.sharingPaused },
          ...prev.kAlerts.map((a) => ageMemberAlert(a)),
        ],
        kUnread: prev.kUnread + 1,
      }))

      if (!before.deviceAccepted || before.sharingPaused) return

      // The manager's side only learns about it after the device has actually
      // reported — the delay is what makes the two-phone demo read as live.
      window.setTimeout(() => {
        setState((prev) => ({
          ...prev,
          alerts: [threat, ...prev.alerts.map(age)],
          unread: prev.m === 'lock' ? prev.unread + 1 : 0,
          m: prev.m === 'lock' ? 'lock' : 'alerts',
          mPush: prev.m === 'lock' ? threat : null,
        }))
      }, CONFIG.alertDelayMs)

      window.setTimeout(() => {
        if (ref.current.m === 'lock') return
        toast(
          'm',
          arch.attributesAlerts
            ? `${threat.title} on ${threat.device}`
            : `${arch.copy.pushPrefix}`
        )
      }, CONFIG.alertDelayMs + 100)
    },
    [toast]
  )

  /**
   * The member switches a protection on or off on their own handset.
   *
   * Switching one off is the interesting direction: under the mapped model the
   * manager is told which device it was and can put it back, which is only
   * possible because the mapping exists. Under zero knowledge nothing is sent —
   * there is no addressable device to attribute it to.
   */
  const setMemberFeature = useCallback(
    (feature: FeatureId, on: boolean) => {
      const before = ref.current
      const arch = ARCHITECTURES[before.arch]

      setState((prev) => ({
        ...prev,
        memberFeatures: { ...prev.memberFeatures, [feature]: on },
        // Turning it back on clears the outstanding alert.
        alerts: on
          ? prev.alerts.map((a) =>
              a.kind === 'feature' && a.feature === feature ? { ...a, resolved: true } : a
            )
          : prev.alerts,
      }))

      if (on) return
      if (!before.deviceAccepted || before.sharingPaused) return
      if (!arch.supportsRemoteFeatureControl) return

      const alert = makeFeatureAlert(feature, CONFIG.memberDevice)
      window.setTimeout(() => {
        setState((prev) => ({
          ...prev,
          flow: 4,
          alerts: [alert, ...prev.alerts.map(age)],
          unread: prev.m === 'lock' ? prev.unread + 1 : 0,
          m: prev.m === 'lock' ? 'lock' : 'alerts',
          mPush: prev.m === 'lock' ? alert : null,
        }))
      }, CONFIG.alertDelayMs)
    },
    []
  )

  /**
   * Dark Web Monitor finds the member's credentials in a dump.
   *
   * Unlike a blocked threat, this is matched against the account rather than
   * the handset — so the manager can be told whose it was under either data
   * model. Nothing here depends on the device mapping.
   */
  const raiseBreach = useCallback(
    (account: string) => {
      const before = ref.current
      const arch = ARCHITECTURES[before.arch]
      const alert = makeBreachAlert(account)

      setState((prev) => ({
        ...prev,
        flow: 4,
        k: 'lock',
        kPush: 'breach',
        breaches: [alert, ...prev.breaches.map(age)],
      }))

      // The member's own Dark Web Monitor works either way. What differs is
      // whether there is any channel to tell the manager.
      if (!before.joined || !arch.sharesAccountAlerts) return

      window.setTimeout(() => {
        setState((prev) => ({
          ...prev,
          alerts: [alert, ...prev.alerts.map(age)],
          unread: prev.m === 'lock' ? prev.unread + 1 : 0,
          m: prev.m === 'lock' ? 'lock' : 'alerts',
          mPush: prev.m === 'lock' ? alert : null,
        }))
      }, CONFIG.alertDelayMs)
    },
    []
  )

  /** The manager turns a protection back on from the alert. */
  const restoreFeature = useCallback(
    (feature: FeatureId) => {
      setState((prev) => ({
        ...prev,
        memberFeatures: { ...prev.memberFeatures, [feature]: true },
        alerts: prev.alerts.map((a) =>
          a.kind === 'feature' && a.feature === feature ? { ...a, resolved: true } : a
        ),
      }))
      toast('m', `${FEATURE_NAMES[feature]} turned back on`)
      toast('k', `Michael turned ${FEATURE_NAMES[feature]} back on`)
    },
    [toast]
  )

  useEffect(() => {
    const t = timers.current
    return () => {
      window.clearTimeout(t.m)
      window.clearTimeout(t.k)
    }
  }, [])

  const api = useMemo<DemoApi>(
    () => ({
      set,
      update,
      toast,
      block,
      setMemberFeature,
      restoreFeature,
      raiseBreach,
      reset,
      setArchitecture,
    }),
    [set, update, toast, block, setMemberFeature, restoreFeature, raiseBreach, reset, setArchitecture]
  )

  return { state, api }
}

/** Lower-case names read better mid-sentence in a toast. */
const FEATURE_NAMES: Record<FeatureId, string> = {
  maliciousSite: 'Malicious website blocker',
  scamCall: 'Scam call protection',
  adTracker: 'Ad and tracker blocker',
}

/** "now" ages to "2 min ago" as soon as a newer event arrives. */
const age = <T extends { time: string }>(x: T): T =>
  x.time === 'now' ? { ...x, time: '2 min ago' } : x

const ageMemberAlert = (a: MemberAlert): MemberAlert =>
  a.threat.time === 'now' ? { ...a, threat: age(a.threat) } : a
